import { formatMoney, type LogEntry } from '@domain';
import { Banner, Card, PageTitle, pillStyle } from '../components/ui.js';
import type { Translate } from '../i18n/index.js';
import type { RenderMessage } from '../i18n/messages.js';
import { theme } from '../theme.js';
import type { Action, AppState } from '../state.js';

interface QueueScreenProps {
  state: AppState;
  t: Translate;
  renderMessage: RenderMessage;
  dispatch: (action: Action) => void;
}

export function QueueScreen({ state, t, renderMessage, dispatch }: QueueScreenProps) {
  const ready = state.queue.filter((q) => q.state === 'READY' && state.selected[q.id]);

  const pushSelected = () => {
    if (!ready.length) return;
    const log: LogEntry[] = ready.map((entry, i) => ({
      id: `n${entry.id}`,
      time: `09:44:0${i}`,
      doc: entry.doc,
      endpoint: entry.endpoint,
      http: '200',
      msg: `Created — Status DRAFT · Idempotency-Key ${entry.id}-8f2c41ab`,
      ok: true,
    }));
    dispatch({ type: 'pushQueue', entries: ready, log });
  };

  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '1200px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
        <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 600, letterSpacing: '-.01em', whiteSpace: 'nowrap' }}>
          {t('Push queue')}
        </h1>
        <div style={{ flex: '1 1 12px' }} />
        <button
          onClick={pushSelected}
          disabled={!ready.length}
          style={{
            background: ready.length ? theme.nav : theme.border,
            color: ready.length ? '#fff' : theme.textSubtle,
            border: 0,
            borderRadius: theme.radiusControl,
            padding: '10px 16px',
            fontWeight: 600,
            fontSize: '12.5px',
            whiteSpace: 'nowrap',
            cursor: ready.length ? 'pointer' : 'default',
          }}
        >
          {ready.length ? `${t('Push')} ${ready.length} ${t('documents')}` : t('Push selected')}
        </button>
      </div>

      <div style={{ color: theme.textMuted, fontSize: '13px', marginBottom: '18px', maxWidth: '640px' }}>
        {t('Documents held here are created in Xero as DRAFT, one API call each, with an Idempotency-Key per document.')}
      </div>

      {state.pushed ? (
        <Banner>
          <span style={{ fontWeight: 600 }}>
            {state.pushed} {t('document(s) created in Xero as DRAFT — see the push log for the response.')}
          </span>
        </Banner>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {state.queue.map((entry) => {
          const selectable = entry.state === 'READY';
          const checked = Boolean(state.selected[entry.id]);
          return (
            <Card key={entry.id} style={{ padding: '14px 16px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 14px' }}>
              <button
                onClick={() => selectable && dispatch({ type: 'toggleSelected', id: entry.id })}
                aria-label="Select"
                style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '6px',
                  flex: '0 0 auto',
                  cursor: selectable ? 'pointer' : 'default',
                  border: checked ? 0 : '1.5px solid #E2E2E5',
                  background: checked ? theme.ink : selectable ? '#fff' : '#F0F0F2',
                  color: '#fff',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: '11px',
                  fontWeight: 700,
                }}
              >
                {checked ? '✓' : ''}
              </button>

              <div style={{ minWidth: '150px', flex: '1 1 190px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  <span style={{ fontFamily: theme.fontMono, fontSize: '13px', fontWeight: 500, color: theme.text }}>{entry.doc}</span>
                  <span style={{ fontSize: '11.5px', color: theme.textMuted }}>{t(entry.type)}</span>
                </div>
                <div style={{ fontSize: '12.5px', color: theme.label, marginTop: '3px' }}>{entry.contact}</div>
              </div>

              <div style={{ flex: '1 1 220px', minWidth: '180px', fontSize: '11.5px', color: theme.textMuted }}>
                {renderMessage(entry.note)}
              </div>
              <div style={{ fontFamily: theme.fontMono, fontSize: '10.5px', color: theme.textFaint, flex: '0 0 auto' }}>{entry.endpoint}</div>
              <div style={{ fontFamily: theme.fontMono, fontSize: '13px', flex: '0 0 auto', minWidth: '92px', textAlign: 'end' }}>
                {formatMoney(entry.amount)}
              </div>
              <span style={pillStyle(entry.state)}>{t(entry.state)}</span>

              <div style={{ display: 'flex', gap: '6px', flex: '0 0 auto' }}>
                <button
                  onClick={() =>
                    dispatch({
                      type: 'go',
                      screen: `form:${entry.type === 'Invoice' ? 'invoice' : entry.type === 'Bill' ? 'bill' : entry.type === 'Quote' ? 'quote' : 'po'}`,
                    })
                  }
                  style={{
                    background: theme.surfaceSunken,
                    border: `1px solid ${theme.border}`,
                    borderRadius: theme.radiusPill,
                    padding: '6px 11px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {t('Open')}
                </button>
                <button
                  onClick={() => dispatch({ type: 'removeFromQueue', id: entry.id })}
                  aria-label="Remove"
                  style={{
                    width: '28px',
                    height: '28px',
                    border: 0,
                    background: 'transparent',
                    borderRadius: theme.radiusPill,
                    color: theme.textFaint,
                    cursor: 'pointer',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </div>
            </Card>
          );
        })}

        {state.queue.length === 0 && (
          <Card style={{ padding: '40px', textAlign: 'center', color: theme.textMuted, fontSize: '13px' }}>
            {t('The queue is empty. Documents you add from a form appear here until you push them.')}
          </Card>
        )}
      </div>
    </div>
  );
}

export function LogScreen({ state, t, dispatch }: QueueScreenProps) {
  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '1200px' }}>
      <PageTitle title={t('Push log')} sub={t('Every call this tool made to Xero, with the response it came back with.')} />
      <div style={{ height: '18px' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {state.log.map((entry) => (
          <Card key={entry.id} style={{ padding: '13px 16px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 14px' }}>
            <span style={{ fontFamily: theme.fontMono, fontSize: '12px', color: theme.textMuted, flex: '0 0 auto' }}>{entry.time}</span>
            <span style={{ fontFamily: theme.fontMono, fontSize: '12.5px', fontWeight: 500, flex: '0 0 auto', minWidth: '96px' }}>{entry.doc}</span>
            <span style={pillStyle(entry.http)}>{entry.http}</span>
            <span style={{ fontFamily: theme.fontMono, fontSize: '10.5px', color: theme.textFaint, flex: '0 0 auto' }}>{entry.endpoint}</span>
            <span style={{ flex: '1 1 240px', minWidth: '200px', fontSize: '12px', color: theme.label }}>{entry.msg}</span>
            {!entry.ok && (
              <button
                onClick={() => dispatch({ type: 'retryLog', id: entry.id })}
                style={{
                  background: theme.surfaceSunken,
                  border: `1px solid ${theme.border}`,
                  borderRadius: theme.radiusPill,
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  flex: '0 0 auto',
                }}
              >
                {t('Retry')}
              </button>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
